import React, { memo, useCallback, useEffect, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTabbedFormConfig } from './hooks/useFormConfig'
import { TabPanel as MuiTabPanel, TabContext, TabList } from '@mui/lab';
import {
  Badge, Box, Card,
  Divider,
  Stack,
  Tab
} from '@mui/material';
import RenderForm from './RenderForm';
import { useTabbedForm, TabbedFormProvider } from 'src/contexts/era-package/TabbedFormContext';
import AbiEntrySummaryApiUrls from "../../../apiUrls/AbiEntrySummaryApiUrls";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDownLeftAndArrowUpRightToCenter as collapseIcon, faArrowUpRightAndArrowDownLeftFromCenter as expandIcon,
  faSquareList as formIcon
} from "@fortawesome/pro-regular-svg-icons";
import Button from "./Button";



const TabPanel = memo(({ value, children }) => (
  <MuiTabPanel value={value} sx={{ px: 0, py: 2 }} >
    <Stack spacing={4}>
      {children}
    </Stack>
  </MuiTabPanel>
));

const Inner = ({
  tabs,
  asyncDefaultValues,
  formConfig, customValidation,
  onSave, onSubmit, readOnly
}) => {
  let { activeTab, setActiveTab } = useTabbedForm()

  const setup = tabs.filter(tab => tab.hasOwnProperty('formProps'))
    .reduce((acc, cur) => {
      acc.fields[cur.key] = cur.formProps.fields
      if (!!cur.formProps.schema)
        acc.schema[cur.key] = cur.formProps.schema
      acc.initialData[cur.key] = cur.formProps.defaultValues
      return acc
    }, { fields: {}, initialData: {}, schema: {} })

  const formMethods = useTabbedFormConfig(formConfig, setup.initialData, setup.schema, customValidation, setup.fields)
  const { errors, isTabDirty } = formMethods
  const [calculateValue, setCalculateValue] = useState(false)

  // Memoize all event handlers to prevent input focus loss
  const handleTabChange = useCallback((event, newValue) => {

    setCalculateValue(false)

    const tabKey = tabs[newValue].key;

    if (tabKey === "confirmation") {

      const currFormValues = formMethods.getValues()
      const {
        importerOfRecordNumber,
        filerCode,
        entryNumber,
        entryType,
        transportationMode,
        entryPort,
        arrivalDate,
        importDate,
        statementDate,
        manualSurcharge,
        informalFee,
        dutiableMailFee
      } = currFormValues.header;

      currFormValues.confirmation = {
        ...currFormValues.confirmation,
        importerOfRecordNumber,
        filerCode,
        entryNumber,
        entryType,
        transportationMode,
        entryPort,
        arrivalDate,
        importDate,
        statementDate,
      }

      formMethods.reset(currFormValues)
      setCalculateValue(true)

      fetch(AbiEntrySummaryApiUrls.GET + `?abiTransId=${asyncDefaultValues.header.abiTransId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
      }).then(res => res.json())
        .then(json => {
          if (!!json.data) {
            let res = json.data

            let resManualSurcharge = res.header.manualSurcharge != null ? res.header.manualSurcharge : 0;
            let resInformalFee = res.header.informalFee != null ? res.header.informalFee : 0;
            let resDutiableMailFee = res.header.dutiableMailFee != null ? res.header.dutiableMailFee : 0;

            let headerDiff = (Number(manualSurcharge != null ? manualSurcharge : 0) - Number(resManualSurcharge)) + (Number(informalFee != null ? informalFee : 0) - Number(resInformalFee)) + (Number(dutiableMailFee != null ? dutiableMailFee : 0) - Number(resDutiableMailFee))

            currFormValues.confirmation = {
              ...currFormValues.confirmation,
              totalAmount: res.confirmation.totalAmount,
              totalDuty: res.confirmation.totalDuty,
              totalMpf: res.confirmation.totalMpf,
              totalHmf: res.confirmation.totalHmf,
              totalOtherFee: Number(res.confirmation.totalOtherFee) + Number(headerDiff),
              totalTaxes: res.confirmation.totalTaxes
            }

            formMethods.reset(currFormValues)
          }

          setCalculateValue(false)
        })
        .catch(err => {
          setCalculateValue(false)
          console.error(err);
        })
    }

    setActiveTab(newValue);
  }, [setActiveTab]);

  // reset defaultValues if async data provided
  useEffect(() => {
    if (!!formMethods && !!asyncDefaultValues)
      formMethods.reset(asyncDefaultValues)
  }, [asyncDefaultValues])

  return <FormProvider {...formMethods}>
    <TabContext value={activeTab}>
      <TabList onChange={handleTabChange} aria-label="Form tabs">
        {tabs.map((tab, index) => {
          const hasError = (formMethods.formState.isSubmitted || isTabDirty(tab.key)) && errors.hasOwnProperty(tab.key)
          return <Tab key={index} value={index} title={`Switch to ${tab.label} tab`} label={<Badge color='error' variant='dot' invisible={!hasError}>
            {tab.label}
          </Badge>} />
        })}
      </TabList>

      <Divider />
      {calculateValue ? (
        <Card>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            gap={1}
          >
            <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
              Loading user data..
            </Box>
          </Box>
        </Card>
      ) : (
        tabs.map((tab, index) => (
          <TabPanel key={index} title={tab.label} value={index}>
            {tab.hasOwnProperty('formProps') && <RenderForm title={tab.label} useRHF
              basicForm
              {...tab.formProps} tabKey={tab.key}
              errors={formMethods.errors?.[tab.key]}
              onSubmit={onSubmit}
              readOnly={tab.formProps.readOnly || readOnly}
            />}
            {tab.hasOwnProperty('render') && tab.render()}
          </TabPanel>
        ))
      )}

    </TabContext>
  </FormProvider>
}

export const RenderTabbedForm = ({ tabs, ...props }) => {
  return <TabbedFormProvider tabs={tabs}>
    <Inner tabs={tabs} {...props} />
  </TabbedFormProvider>
}